import React, { useEffect, useState } from "react";
import styles from "./FacultySpotlight.module.css";
import Pagination from "../Pagination/Pagination";
import Image from "next/image";

interface FilterData {
  school?: string;
  program?: string;
  resetPage?: boolean;
}

interface Faculty {
  id: number;
  school: string;
  program: string;
  card_with_img: {
    id: number;
    title_description: {
      id: number;
      title: string;
      description: string;
    };
    images: {
      id: number;
      url: string;
      alternativeText: string;
    }[];
  };
}

interface FacultySpotlightProps {
  data: {
    id: number;
    title: string;
    cards: Faculty[];
  };
  filterData: FilterData;
}

const FacultySpotlight: React.FC<FacultySpotlightProps> = ({
  data,
  filterData,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [isMobile, setIsMobile] = useState(false);

  // Reset page when filter data changes or component mounts
  useEffect(() => {
    if (filterData?.resetPage) {
      setCurrentPage(1);
    }
  }, [filterData]);

  // Always reset to page 1 on initial load/reload
  useEffect(() => {
    setCurrentPage(1);
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const handleResize = () => {
        setIsMobile(window.innerWidth <= 767);
      };

      handleResize();
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }
  }, []);

  const itemsPerPage = isMobile ? 3 : 6;

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const startIndex = (currentPage - 1) * itemsPerPage;

  // Ensure data is available and default to an empty array if not
  const facultyCards = data?.cards || [];

  // Filter faculty data based on the filterData
  const filteredData = facultyCards?.filter((faculty) => {
    const matchesSchool = filterData?.school
      ? faculty?.school === filterData?.school
      : true;
    const matchesProgram = filterData?.program
      ? faculty?.program === filterData?.program
      : true;

    return matchesSchool && matchesProgram;
  });

  // If both school and program are empty, show all cards
  const displayedData =
    filterData?.school === "" && filterData?.program === ""
      ? facultyCards
      : filteredData;
  // Handle potential empty data
  const selectedItems = displayedData.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  return (
    <div className={styles.facultySpotlight}>
      <div className="container">
        <h2 className={styles.heading}>Faculty Spotlights</h2>
        {displayedData?.length === 0 ? (
          <p className={styles.noFacultyMessage}>No faculty available</p>
        ) : (
          <div className={styles.grid}>
            {selectedItems?.map((faculty) => (
              <div key={faculty?.id} className={styles.card}>
                <div className={styles.imgCard}>
                  <Image
                    src={
                      faculty?.card_with_img.images[0]?.url ||
                      "/assets/default.png"
                    }
                    alt={
                      faculty?.card_with_img.images[0]?.alternativeText ||
                      "faculty_img"
                    }
                    title={
                      faculty?.card_with_img.images[0]?.alternativeText ||
                      "faculty_img"
                    }
                    className={styles.image}
                    width={380}
                    height={320}
                    priority
                  />
                </div>
                <h3 className={styles.name}>
                  {faculty?.card_with_img?.title_description?.title}
                </h3>
                <p className={styles.title}>
                  {faculty?.card_with_img?.title_description?.description}
                </p>
              </div>
            ))}
          </div>
        )}
        {displayedData?.length > (isMobile ? 3 : 6) && (
          <Pagination
            totalItems={displayedData?.length}
            itemsPerPage={itemsPerPage}
            currentPage={currentPage}
            onPageChange={handlePageChange}
          />
        )}
      </div>
    </div>
  );
};

export default FacultySpotlight;
